import { useCallback, useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { Sparkle, PencilSimple, ArrowClockwise, CheckCircle, ChartBar } from "@phosphor-icons/react";

/** The optional 3-question understanding check on a document: switch it on (AI writes the questions), edit or regenerate them. */
export default function HseQuizCard({ doc, onChanged }) {
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(false);
  const [ins, setIns] = useState(null);
  const load = useCallback(() => API.get(`/hse/documents/${doc.id}/quiz`).then(({ data }) => { setQ(data); if (data.enabled && data.attempts > 0) API.get(`/hse/documents/${doc.id}/quiz/insights`).then(r => setIns(r.data)).catch(() => {}); }).catch(() => {}), [doc.id]);
  useEffect(() => { load(); }, [load]);
  const pending = q?.ai_status === "pending";
  useEffect(() => {
    if (!pending) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [pending, load]);
  if (!q || doc.status === "draft") return null;

  const toggle = async (enabled) => {
    setBusy(true);
    try {
      const { data } = await API.put(`/hse/documents/${doc.id}/quiz`, { enabled });
      setQ(data);
      toast.success(enabled ? (data.generating ? "Understanding check on — the AI is writing three questions from the document" : "Understanding check on") : "Understanding check off");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const regenerate = async () => {
    try { await API.post(`/hse/documents/${doc.id}/quiz/regenerate`); setQ(x => ({ ...x, ai_status: "pending" })); toast.success("Rewriting the questions…"); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 text-xs space-y-3" data-testid="hse-quiz-card">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Understanding check</p>
          <p className="text-muted-foreground mt-0.5">Three questions operatives must get right before they can sign. {q.enabled && q.attempts > 0 ? `${q.passed_attempts}/${q.attempts} attempts passed.` : ""}</p>
        </div>
        <Switch checked={!!q.enabled} disabled={busy} onCheckedChange={toggle} data-testid="hse-quiz-toggle" />
      </div>
      {q.enabled && (
        <>
          {pending && <p className="inline-flex items-center gap-1 text-violet-700" data-testid="hse-quiz-pending"><Sparkle size={12} weight="fill" className="animate-pulse" /> AI is writing the questions…</p>}
          {q.ai_status === "failed" && <p className="text-red-700" data-testid="hse-quiz-failed">Couldn't write the questions: {q.error}. Try again or write them yourself.</p>}
          {!!q.questions.length && (
            <ol className="space-y-1.5 list-decimal pl-4" data-testid="hse-quiz-questions">
              {q.questions.map(x => <li key={x.id}><span className="font-medium">{x.question}</span><span className="text-muted-foreground block">Answer: {x.options[x.answer]}{x.section ? ` · ${x.section}` : ""}</span></li>)}
            </ol>
          )}
          {ins && ins.attempts > 0 && (
            <div className="border-t border-border pt-2 space-y-1.5" data-testid="hse-quiz-insights">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground inline-flex items-center gap-1"><ChartBar size={11} /> What isn't landing</p>
              <p className="text-muted-foreground">{ins.people} operative{ins.people === 1 ? "" : "s"} · {ins.attempts} attempt{ins.attempts === 1 ? "" : "s"}{ins.first_time_pass_pct !== null ? ` · ${ins.first_time_pass_pct}% passed first time` : ""}{ins.avg_attempts_to_pass ? ` · ${ins.avg_attempts_to_pass} tries to pass on average` : ""}</p>
              {ins.questions.filter(x => x.answered).map(x => (
                <div key={x.id} data-testid={`hse-quiz-insight-${x.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="flex-1 truncate" title={x.question}>{x.question}</span>
                    <span className={`font-semibold tabular-nums ${x.wrong_pct >= 50 ? "text-red-600" : x.wrong_pct > 0 ? "text-amber-600" : "text-emerald-600"}`}>{x.wrong_pct}% wrong</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden mt-0.5"><div className={`h-full ${x.wrong_pct >= 50 ? "bg-red-500" : "bg-amber-400"}`} style={{ width: `${x.wrong_pct}%` }} /></div>
                  {x.wrong > 0 && <p className="text-[11px] text-muted-foreground mt-0.5">{x.wrong} wrong of {x.answered}{x.most_picked_wrong ? ` · most picked “${x.most_picked_wrong}”` : ""}{x.section ? ` · re-read “${x.section}”` : ""}{x.people_struggling ? ` · ${x.people_struggling} still not got it` : ""}</p>}
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" className="h-8 flex-1" onClick={() => setEdit(true)} disabled={pending} data-testid="hse-quiz-edit-btn"><PencilSimple size={13} className="mr-1" /> {q.questions.length ? "Edit questions" : "Write questions"}</Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={regenerate} disabled={pending} title="Ask the AI for a fresh set" data-testid="hse-quiz-regen-btn"><ArrowClockwise size={13} /></Button>
          </div>
        </>
      )}
      <QuizEditor docId={doc.id} open={edit} onOpenChange={setEdit} questions={q.questions} onSaved={(data) => { setQ(x => ({ ...x, ...data })); onChanged?.(); }} />
    </div>
  );
}

const blank = () => ({ id: Math.random().toString(36).slice(2, 10), question: "", options: ["", "", "", ""], answer: 0, section: "", explanation: "" });

function QuizEditor({ docId, open, onOpenChange, questions, onSaved }) {
  const [qs, setQs] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setQs(questions?.length ? questions.map(x => ({ ...x, options: [...x.options, "", "", "", ""].slice(0, 4) })) : [blank(), blank(), blank()]); }, [open, questions]);
  const set = (i, patch) => setQs(a => a.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const setOpt = (i, k, v) => setQs(a => a.map((x, j) => (j === i ? { ...x, options: x.options.map((o, m) => (m === k ? v : o)) } : x)));
  const save = async () => {
    setBusy(true);
    try {
      const payload = qs.map(x => {
        const kept = x.options.map((o, k) => ({ o, k })).filter(({ o }) => o.trim());
        const answer = kept.findIndex(({ k }) => k === x.answer);
        return { ...x, options: kept.map(({ o }) => o), answer };
      });
      const bad = payload.findIndex(x => x.answer < 0);
      if (bad >= 0) { toast.error(`Question ${bad + 1}: the option marked correct is blank — fill it in or mark another`); setBusy(false); return; }
      const { data } = await API.put(`/hse/documents/${docId}/quiz/questions`, { questions: payload });
      onSaved(data); onOpenChange(false); toast.success("Questions saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="hse-quiz-editor">
        <DialogHeader><DialogTitle className="text-base">Understanding check questions</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {qs.map((x, i) => (
            <div key={x.id} className="border border-border rounded-lg p-3 space-y-2" data-testid={`hse-quiz-q-${i}`}>
              <div className="flex items-center justify-between"><p className="text-xs font-semibold">Question {i + 1}</p>{qs.length > 1 && <button type="button" className="text-xs text-muted-foreground hover:text-red-600" onClick={() => setQs(a => a.filter((_, j) => j !== i))} data-testid={`hse-quiz-q-remove-${i}`}>Remove</button>}</div>
              <Textarea value={x.question} onChange={e => set(i, { question: e.target.value })} rows={2} placeholder="What must you do when…?" className="text-sm" data-testid={`hse-quiz-q-text-${i}`} />
              <div className="grid sm:grid-cols-2 gap-1.5">
                {x.options.map((o, k) => (
                  <label key={k} className={`flex items-center gap-2 border rounded-md px-2 h-9 ${x.answer === k ? "border-emerald-400 bg-emerald-50" : "border-border"}`}>
                    <input type="radio" name={`ans-${x.id}`} checked={x.answer === k} onChange={() => set(i, { answer: k })} title="Correct answer" data-testid={`hse-quiz-q-correct-${i}-${k}`} />
                    <Input value={o} onChange={e => setOpt(i, k, e.target.value)} placeholder={`Option ${String.fromCharCode(65 + k)}`} className="h-7 border-0 px-0 text-xs focus-visible:ring-0" data-testid={`hse-quiz-q-opt-${i}-${k}`} />
                    {x.answer === k && <CheckCircle size={14} weight="fill" className="text-emerald-600 shrink-0" />}
                  </label>
                ))}
              </div>
              <Input value={x.section || ""} onChange={e => set(i, { section: e.target.value })} placeholder="Section to re-read if wrong (optional)" className="h-8 text-xs" data-testid={`hse-quiz-q-section-${i}`} />
            </div>
          ))}
          {qs.length < 5 && <Button size="sm" variant="outline" onClick={() => setQs(a => [...a, blank()])} data-testid="hse-quiz-q-add">Add question</Button>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="hse-quiz-save-btn">{busy ? "Saving…" : "Save questions"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
